import { useCallback, useSyncExternalStore } from "react";

import { useProvidedChatStream } from "../context/ChatStreamContext";
import {
  ChatStreamState,
  ChatStreamStore,
  createStreamingStore,
  StreamingState,
} from "../stores/StreamingStore";
import { ChatStreamSurface, useStreamBindings } from "./useStreamBindings";
import { StoreApi } from "zustand";

/** Read/act handle on the host's stream. No setStreamConfig — owner-only. */
export type ChatStreamHandle = Omit<ChatStreamSurface, "setStreamConfig"> & {
  store: ChatStreamStore;
};

// Never-streaming placeholder so binding hooks run unconditionally when no
// store resolves; its state and actions are never handed to callers.
let detachedStore: StoreApi<StreamingState> | null = null;
const getDetachedStore = () => {
  detachedStore ??= createStreamingStore({});
  return detachedStore;
};

function useResolvedStream(): StoreApi<StreamingState> | null {
  // Every host owns its store and provides it; there is no key-based lookup.
  return useProvidedChatStream();
}

function useHandle(
  store: StoreApi<StreamingState> | null,
): ChatStreamHandle | null {
  const bindings = useStreamBindings(store ?? getDetachedStore());
  if (!store) return null;
  const { setStreamConfig: _ownerOnly, ...handle } = bindings;
  return { ...handle, store };
}

/**
 * Descendant hook: attach to the host chat's stream without owning it.
 * Expects a ChatStreamProvider above; null means the host forgot one
 * (dev-warned) — components legitimately outside any chat use
 * useChatStreamIfAvailable instead.
 */
export function useChatStream(): ChatStreamHandle | null {
  const store = useResolvedStream();
  if (import.meta.env.DEV && !store) {
    console.warn(
      "[useChatStream] no ChatStreamProvider above — the host must own " +
        "its store (useOwnChatStream) and provide it",
    );
  }
  return useHandle(store);
}

/** useChatStream for components that may render outside any chat provider. */
export function useChatStreamIfAvailable(): ChatStreamHandle | null {
  return useHandle(useResolvedStream());
}

/**
 * Resolve the host chat's store without binding to it — for cards that only
 * read/act imperatively and must not re-render on every stream update. The
 * descendant view carries no setState or setStreamConfig: a card that ran an
 * owner hook instead would setStreamConfig its own minimal config onto the
 * host surface's store (product, view-context getters, handlers) and break
 * product chats.
 */
export function useChatStreamStore(): ChatStreamStore | null {
  return useResolvedStream();
}

/**
 * Reactive read of a value from the host chat's stream, or `fallback` when
 * no store resolves. The selector must return a reference stored in state
 * (not a freshly built object) so snapshots stay stable.
 */
export function useChatStreamValue<T>(
  selector: (state: ChatStreamState) => T,
  fallback: T,
): T {
  const store = useResolvedStream();
  const subscribe = useCallback(
    (onChange: () => void) => (store ? store.subscribe(onChange) : () => {}),
    [store],
  );
  return useSyncExternalStore(subscribe, () =>
    store ? selector(store.getState()) : fallback,
  );
}
